import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DisplayGuestDto, DisplayUserDto, VisitorDto } from '@picflow/types';
import { Subject, catchError, map, of, throwError } from 'rxjs';
import { visitorsCacheMaxAge } from '../consts';
import { AppConfigService } from '../providers/config.service';
import { FeatureFlagsService } from './feature-flags.service';
import { GalleryPasswordService } from './gallery-password.service';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { StateService } from './state.service';

export interface DisplayUserEmailDto extends DisplayUserDto {
  email: string;
}

export interface DisplayGuestEmailDto extends DisplayGuestDto {
  email: string;
}

export interface visitorResponse extends Omit<VisitorDto, 'guest' | 'user'> {
  user: DisplayUserEmailDto;
  guest: DisplayGuestEmailDto;
}

@Injectable({
  providedIn: 'root',
})
export class VisitorService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(ObservableDataLoaderService);
  private state = inject(StateService);
  private featureFlags = inject(FeatureFlagsService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private getAllService = inject(GetAllService);

  public createVisitor$ = new Subject<string>();

  private onCreateVisitor = this.createVisitor$.subscribe(galleryId => {
    const email = this.state.user?.email;
    if (email && ['@nusign.com', '@picflow.com'].find(_email => email.endsWith(_email))) {
      // preventing internal views as counting unless spesifically allowed
      if (!this.featureFlags.get('forceGalleryViews')) return;
    }
    // using loader just to make sure that we don't do this request multiple times per session
    return this.loader.get(`create_visitor_${galleryId}`, () => {
      return this.http
        .post(`${this.config.apiEndpoint}/visitors${this.getPassword(galleryId)}`, { gallery: galleryId })
        .pipe(
          catchError(err => {
            if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
            if (err.status !== 400 && err.status !== 403) return throwError(() => err);
            if (err.error.code !== 'visitor_exists') return throwError(() => err);
            return of(true);
          })
        );
    });
  });

  public getAllVisitors() {
    const url = `${this.config.apiEndpoint}/visitors?expand[]=data.user&expand[]=data.guest`;
    return this.loader.get(
      'visitors',
      () => this.getAllService.getAll<visitorResponse>(url).pipe(map(visitors => this.makeUnique(visitors))),
      visitorsCacheMaxAge
    );
  }

  public getGalleryVisitors(galleryId: string) {
    const url = `${this.config.apiEndpoint}/visitors?expand[]=data.user&expand[]=data.guest&gallery=${galleryId}`;

    return this.loader.get(
      `visitors_${galleryId}`,
      () =>
        this.getAllService.getAll<visitorResponse>(url).pipe(
          map(visitors => {
            return this.makeUnique(visitors);
          })
        ),
      visitorsCacheMaxAge
    );
  }

  private makeUnique(visitors: visitorResponse[]): visitorResponse[] {
    const unique = [];
    visitors.forEach(visitor => {
      if (
        !unique.find(_visitor => {
          if (visitor.user && _visitor.user) return _visitor.user.id === visitor.user.id;
          if (visitor.guest && _visitor.guest) return _visitor.guest.id === visitor.guest.id;
          return false;
        })
      ) {
        unique.push(visitor);
      }
    });
    return unique;
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '?password=' + encodeURIComponent(password);
  }
}
